// Lightweight create-X modals — group, topic, channel. The bigger modals
// (Settings, Balance, NewPost) live in their own files because they each
// have substantial internal state.
import { useState } from "react";
import { api } from "../api.js";
import { T, T_NEW } from "../theme.js";
import { I } from "../icons.jsx";
import { Btn, Inp, Modal } from "./ui.jsx";
import { useT } from "../i18n.jsx";

export function NewGroupModal({ onClose, onDone }) {
  const t = useT();
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  return (
    <Modal title={t("modals.group.title")} onClose={onClose}>
      <Inp label={t("modals.group.name")} value={name} onChange={setName}
           placeholder={t("modals.group.name.placeholder")} autoFocus icon={I.group?.()} />
      <Inp label={t("modals.group.desc")} value={desc} onChange={setDesc}
           placeholder={t("modals.group.desc.placeholder")} />
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: T_NEW.s2 }}>
        <Btn variant="ghost" onClick={onClose}>{t("modals.cancel")}</Btn>
        <Btn disabled={!name.trim()}
             onClick={async () => { await api.createGroup(name.trim(), desc.trim()); onDone(); onClose(); }}>
          {t("modals.group.create")}
        </Btn>
      </div>
    </Modal>
  );
}

export function NewTopicModal({ groupId, onClose, onDone }) {
  const t = useT();
  const [name, setName] = useState("");
  return (
    <Modal title={t("modals.topic.title")} onClose={onClose}>
      <Inp label={t("modals.topic.name")} value={name} onChange={setName}
           placeholder={t("modals.topic.name.placeholder")} autoFocus icon={I.hash()} />
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: T_NEW.s2 }}>
        <Btn variant="ghost" onClick={onClose}>{t("modals.cancel")}</Btn>
        <Btn disabled={!name.trim()}
             onClick={async () => { await api.createTopic(groupId, name.trim()); onDone(); onClose(); }}>
          {t("modals.topic.add")}
        </Btn>
      </div>
    </Modal>
  );
}

export function NewChannelModal({ onClose, onDone }) {
  const t = useT();
  const [slug, setSlug] = useState("");
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  // Feature 7: organizer fee % stored per channel. Defaults to 0
  // (legacy behaviour); operator can later edit via PATCH /channels/:id.
  const [feePct, setFeePct] = useState("0");
  // BuyerGram in-house cashback % (migration 0028). Comes out of
  // organizer-fee margin; surfaced to the customer as pending then
  // earned on delivered.
  const [cashbackPct, setCashbackPct] = useState("0");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  // Track whether the user has manually edited the slug field. Once they
  // have, we stop auto-syncing from the name so we don't overwrite their input.
  const [slugTouched, setSlugTouched] = useState(false);
  const slugify = (v) => v.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40);
  const submit = async () => {
    setErr(""); setLoading(true);
    try {
      const r = await api.createChannel({ slug: slug.trim().toLowerCase(), name: name.trim(), description: desc.trim() });
      // Apply fee + cashback with a second call since the create
      // endpoint doesn't accept them yet — uses the PATCH path which does.
      const patch = {};
      const pct = parseFloat(feePct);
      const cb = parseFloat(cashbackPct);
      if (Number.isFinite(pct) && pct > 0) patch.organizerFeePct = pct;
      if (Number.isFinite(cb) && cb > 0) patch.cashbackPct = cb;
      if (Object.keys(patch).length > 0 && r?.id) {
        await api.updateChannel(r.id, patch);
      }
      onDone(); onClose();
    } catch (e) { setErr(e.message); }
    setLoading(false);
  };
  return (
    <Modal title={t("modals.channel.title")} onClose={onClose}>
      <Inp label={t("modals.channel.name")} value={name}
           onChange={(v) => { setName(v); if (!slugTouched) setSlug(slugify(v)); }}
           placeholder={t("modals.channel.name.placeholder")} autoFocus icon={I.hash()} />
      <Inp label={t("modals.channel.slug")} value={slug}
           onChange={(v) => { setSlugTouched(true); setSlug(v); }}
           placeholder={t("modals.channel.slug.placeholder")} />
      <Inp label={t("modals.channel.desc")} value={desc} onChange={setDesc} placeholder={t("modals.channel.desc.placeholder")} />
      <Inp label={t("modals.channel.fee")} value={feePct} onChange={setFeePct}
           placeholder={t("modals.channel.fee.placeholder")} />
      <Inp label={t("modals.channel.cashback")} value={cashbackPct} onChange={setCashbackPct}
           placeholder={t("modals.channel.cashback.placeholder")} />
      {err && (
        <div style={{ color: T_NEW.danger, fontSize: T_NEW.meta, marginBottom: 12,
                      padding: "10px 12px", background: `${T_NEW.danger}14`,
                      border: `1px solid ${T_NEW.danger}33`, borderRadius: T_NEW.r1,
                      fontFamily: T_NEW.fontSans }}>
          {err}
        </div>
      )}
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: T_NEW.s2 }}>
        <Btn variant="ghost" onClick={onClose}>{t("modals.cancel")}</Btn>
        <Btn disabled={!name.trim() || !slug.trim() || loading} onClick={submit}>
          {loading ? t("modals.channel.creating") : t("modals.channel.create")}
        </Btn>
      </div>
    </Modal>
  );
}
